import type * as toastify from 'react-toastify';

import { useCallback } from 'react';
import { styled } from 'styled-components';

import { TextButton } from '@creditornot/cb-button';
import { AlertCircleFilled, CheckmarkCircleFilled, InfoCircleFilled } from '@creditornot/cb-icons';
import { colors, typographyCss } from '@creditornot/cb-ingredients';

export type ToastProps = {
  title: string;
  content: React.ReactNode;
  severity: 'error' | 'info' | 'success' | 'warning';
  onClose?: () => void;
  closeTextButton?: string;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  gap: 16px;
  display: flex;

  ${typographyCss.Title2()};
`;
const Content = styled.div`
  ${typographyCss.Body3()}
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const InfoIcon = styled(InfoCircleFilled)`
  fill: ${colors.wolt100};
  display: block;
  width: 20px;
`;

const SuccessIcon = styled(CheckmarkCircleFilled)`
  fill: ${colors.lime100};
  display: block;
  width: 20px;
`;
const ErrorIcon = styled(AlertCircleFilled)`
  fill: ${colors.strawberry100};
  display: block;
  width: 20px;
`;

const WarningIcon = styled(AlertCircleFilled)`
  fill: ${colors.orange100};
  display: block;
  width: 20px;
`;

const variantConfigs: Record<
  ToastProps['severity'],
  {
    icon: React.ReactNode;
  }
> = {
  error: {
    icon: <ErrorIcon />,
  },
  info: {
    icon: <InfoIcon />,
  },
  success: {
    icon: <SuccessIcon />,
  },
  warning: {
    icon: <WarningIcon />,
  },
};

const ToastContainer = ({ closeToast, data }: Partial<toastify.ToastContentProps<ToastProps>>) => {
  const handleOnClose = useCallback(() => {
    closeToast?.();
    data?.onClose?.();
  }, [closeToast, data]);
  return (
    <Wrapper>
      <Header>
        {data?.severity && variantConfigs?.[data.severity].icon}
        {data?.title}
      </Header>
      <Content>{data?.content}</Content>
      <Footer>
        {data?.closeTextButton && (
          <TextButton onClick={handleOnClose}>{data?.closeTextButton}</TextButton>
        )}
      </Footer>
    </Wrapper>
  );
};

export default ToastContainer;
